import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import { Button, CardActions, CircularProgress, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useTranslate, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { buildUserLookupConfig } from "./userLookupConfig";
import UserInflightDetailsTab, { InflightDetailsPayload } from "./UserInflightDetailsTab";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
    },
    form: {
        padding: "0 1em 1em 1em",
        width: "100%",
        maxWidth: 520,
        margin: "0 auto",
    },
    input: {
        marginTop: "1em",
        width: "100%",
    },
    actions: {
        padding: "0 1em 1em 1em",
        width: "100%",
        maxWidth: 520,
        margin: "0 auto",
    },
}));

const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />
);

interface FormValues {
    EmailOrId?: string | number;
}

const { Form } = withTypes<FormValues>();

const parseInflightResponse = (data: unknown): InflightDetailsPayload | null => {
    if (!data) {
        return null;
    }
    if (Array.isArray(data)) {
        return (data[0] as InflightDetailsPayload) || null;
    }
    return data as InflightDetailsPayload;
};

const UserInflightDetails = () => {
    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(false);
    const [lookupValue, setLookupValue] = useState<string | number>("");
    const [hasSearched, setHasSearched] = useState(false);
    const [inflightData, setInflightData] = useState<InflightDetailsPayload | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = (inputData: FormValues) => {
        const { EmailOrId } = inputData;
        if (!EmailOrId) {
            return;
        }

        setLookupValue(EmailOrId);
        setHasSearched(true);
        setLoading(true);
        setInflightData(null);
        setError(null);
        notify("pos.fetching_the_result");

        const config = buildUserLookupConfig(EmailOrId);

        axios
            .get(ENDPOINTS.USER_INFLIGHT_DETAILS, config)
            .then((res) => {
                setInflightData(parseInflightResponse(res?.data));
                notify("Inflight details loaded", "info", "", undefined, 5000);
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.message ||
                        err?.message ||
                        "Failed to load inflight details"
                );
                notify(
                    err?.response?.data?.message || "Failed to load inflight details",
                    "error",
                    "",
                    undefined,
                    50000
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const validate = (values: FormValues) => {
        const errors: FormValues = {};
        if (!values.EmailOrId) {
            errors.EmailOrId = translate("ra.validation.required");
        }
        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            render={({ handleSubmit: submitForm }) => (
                <div>
                    <form onSubmit={submitForm} noValidate>
                        <div className={classes.main}>
                            <div className={classes.form}>
                                <div className={classes.input}>
                                    <Field
                                        autoFocus
                                        name="EmailOrId"
                                        // @ts-ignore
                                        component={renderInput}
                                        label={translate("pos.buttons.email_or_user_id")}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <CardActions className={classes.actions}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={loading}
                                    fullWidth
                                >
                                    {loading && <CircularProgress size={25} thickness={2} />}
                                    Fetch Inflight Details
                                </Button>
                            </CardActions>
                            <Notification />
                        </div>
                    </form>

                    <UserInflightDetailsTab
                        data={inflightData}
                        loading={loading}
                        error={error}
                        lookupValue={lookupValue}
                        hasSearched={hasSearched}
                    />
                </div>
            )}
        />
    );
};

export default UserInflightDetails;
